import { useTranslation, Trans } from 'react-i18next';
import { Link } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import { Icon, PageHeader, BtnPrimary, BtnSecondary } from '../regureStyles';
import DataError from './DataError';
import Feedback from './Feedback';
import FilesTable from './FilesTable';
import Filter from './Filter';
import InfoBanner from './InfoBanner';
import LinkToClaim from './LinkToClaim';
import LinkToClient from './LinkToClient';
import Stats from './Stats';
import useDocumentFunctions from './useDocumentFunctions';

const UnmatchedDocumentsScreen = () => {
  const { t } = useTranslation();
  const {
    loading,
    dataError,
    selected,
    setSelected,
    linkClaimId,
    setLinkClaimId,
    linking,
    search,
    setSearch,
    filterType,
    setFilterType,
    uploading,
    uploadMsg,
    fileInputRef,
    fetchDocs,
    docTypes,
    filtered,
    handleLink,
    handleUpload,
    stats,
    attachedCount,
    canLinkToClaim,
    canLinkToClient,
    clientPickerFor,
    setClientPickerFor,
    linkClientId,
    setLinkClientId,
    availableClients,
    loadClientsForPicker,
    handleLinkClient,
    canUploadDocs,
    page,
    setPage,
    pageSize,
    total,
    pages,
  } = useDocumentFunctions();

  return (
    <AppLayout>
      <div style={{ flex: 1, overflow: 'auto', padding: '32px 40px' }}>
        <PageHeader
          title={t('screens.unmatchedDocs.title')}
          subtitle={t('screens.unmatchedDocs.subtitle')}
          actions={
            <div style={{ display: 'flex', gap: 10 }}>
              <BtnSecondary onClick={fetchDocs}>
                <Icon name='refresh' size={15} />
                {t('screens.unmatchedDocs.refresh')}
              </BtnSecondary>
              {canUploadDocs && (
                <>
                  <input ref={fileInputRef} type='file' accept='.pdf,.doc,.docx,.png,.jpg,.jpeg' multiple style={{ display: 'none' }} onChange={handleUpload} />
                  <BtnPrimary onClick={() => fileInputRef.current.click()} disabled={uploading}>
                    <Icon name='upload' size={15} />
                    {uploading ? t('screens.unmatchedDocs.uploading') : t('screens.unmatchedDocs.uploadDocument')}
                  </BtnPrimary>
                </>
              )}
            </div>
          }
        />

        {/* Data error banner */}
        {dataError && <DataError fetchDocs={fetchDocs} />}

        {/* Upload feedback */}
        {uploadMsg && <Feedback uploadMsg={uploadMsg} />}

        {/* Stats */}
        <Stats stats={stats} />

        {/* Info Banner */}
        <InfoBanner />

        {/* Attached-docs hint. Only rendered when the tenant has docs that
            ARE attached to clients — otherwise the near-empty unmatched
            queue reads as "the system is empty" (Test Run 7-22: Minnesota
            tester saw 2 rows and concluded the seed had failed, when in
            fact 25 seeded docs were pre-attached to clients by design).
            Tenant-agnostic — the count is live from the tenant's own
            fileexchanges collection. The linked "Clients → Documents"
            portion uses <Trans> so translators can position it anywhere
            in the sentence for languages with different word order. */}
        {attachedCount > 0 && (
          <div
            style={{
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: 10,
              padding: '10px 16px',
              marginBottom: 24,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              fontSize: 13,
              color: '#1e40af',
            }}
          >
            <Icon name='link' size={16} color='#2563eb' />
            <span>
              <Trans
                i18nKey='screens.unmatchedDocs.attachedHint'
                count={attachedCount}
                values={{ count: attachedCount }}
                components={{
                  clientsLink: <Link to='/clients' style={{ color: '#1d4ed8', fontWeight: 600, textDecoration: 'underline' }} />,
                }}
              />
            </span>
          </div>
        )}

        {/* Filters */}
        <Filter search={search} setSearch={setSearch} filterType={filterType} setFilterType={setFilterType} docTypes={docTypes} filtered={filtered} />

        {/* Table */}
        <FilesTable
          loading={loading}
          filtered={filtered}
          setSelected={setSelected}
          setLinkClaimId={setLinkClaimId}
          canLinkToClaim={canLinkToClaim}
          canLinkToClient={canLinkToClient}
          setClientPickerFor={setClientPickerFor}
          setLinkClientId={setLinkClientId}
          loadClientsForPicker={loadClientsForPicker}
        />

        {/* Pagination controls — backend returns { total, page, pages }.
            Rendered whenever there's more than one page of results.
            Enterprise-standard: same shape as the Clients screen pager. */}
        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
            <span style={{ fontSize: 13, color: '#64748b' }}>
              {t('screens.unmatchedDocs.pagination.pageOf', { page, pages, count: total })}
            </span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: page <= 1 ? 'not-allowed' : 'pointer', color: page <= 1 ? '#cbd5e1' : '#334155', fontSize: 13 }}
              >
                {t('screens.unmatchedDocs.pagination.prev')}
              </button>
              {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                // Compact window of pages around current, matching ClientsListScreen behaviour.
                const start = Math.max(1, Math.min(pages - 6, page - 3));
                const p = start + i;
                if (p > pages) return null;
                const isCurrent = p === page;
                return (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    style={{ width: 32, height: 32, borderRadius: 6, border: isCurrent ? '1px solid #800020' : '1px solid #e2e8f0', background: isCurrent ? 'rgba(128,0,32,0.07)' : '#fff', color: isCurrent ? '#800020' : '#334155', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => setPage(Math.min(pages, page + 1))}
                disabled={page >= pages}
                style={{ padding: '6px 12px', borderRadius: 6, border: '1px solid #e2e8f0', background: '#fff', cursor: page >= pages ? 'not-allowed' : 'pointer', color: page >= pages ? '#cbd5e1' : '#334155', fontSize: 13 }}
              >
                {t('screens.unmatchedDocs.pagination.next')}
              </button>
            </div>
          </div>
        )}

        {/* Link to Claim modal — only mounted when the tenant has claims enabled. */}
        {canLinkToClaim && (
          <LinkToClaim
            selected={selected}
            linkClaimId={linkClaimId}
            setLinkClaimId={setLinkClaimId}
            setSelected={setSelected}
            handleLink={handleLink}
            linking={linking}
          />
        )}
        {/* Attach to Client modal — only mounted when the tenant has clients enabled. */}
        {canLinkToClient && (
          <LinkToClient
            selected={clientPickerFor}
            setSelected={setClientPickerFor}
            linkClientId={linkClientId}
            setLinkClientId={setLinkClientId}
            handleLinkClient={handleLinkClient}
            linking={linking}
            availableClients={availableClients}
          />
        )}
      </div>
    </AppLayout>
  );
};

export default UnmatchedDocumentsScreen;
